import { useCallback, useEffect, useState } from "react"
import { useNavigate } from 'react-router-dom'
import { Button, Modal } from "react-bootstrap"
import { newMessageSender } from "../../core/api"
import { MessageChannel } from "../../core/types"
import { IMessageSender, IRequisiteType } from "../../core/response.types"
import Select from 'react-select'
import { KTIcon } from "../../../../../../../_metronic/helpers"
import Swal from "sweetalert2"
import { SelectStyles } from "../../../../../../../components/Select"
import { useThemeMode } from "../../../../../../../_metronic/partials"
import { getGeneralError } from '../../../../../../common/api/errors'


interface Props {
  show: boolean
  onClose: (open: boolean) => void
  onSave?: (sender: IMessageSender) => void
  requisiteTypes: IRequisiteType[]
  initialSender: string
}

export default ({ show, onClose, onSave, requisiteTypes, initialSender }: Props) => {
    const { mode: theme } = useThemeMode()
    const navigate = useNavigate()

    // ===== ДАННЫЕ ФОРМЫ =====
    const [requisiteType, setRequisiteType] = useState<IRequisiteType | null>(null)
    const [sender, setSender] = useState(initialSender)
    const [channel, setChannel] = useState<MessageChannel>('SMS')

    // ===== UI СОСТОЯНИЯ =====
    const [isSaving, setIsSaving] = useState(false)

    const handleSelectRequisiteType = useCallback((selectedRequisiteType: any) => {
        if(selectedRequisiteType === null) {
            setRequisiteType(null)
            return
        }
        var reqTypeFind = requisiteTypes.find(v => v.code === selectedRequisiteType!.value)
        if(reqTypeFind !== undefined) setRequisiteType(reqTypeFind)
    }, [])

    const handleAddRequisiteType = useCallback((value: string) => {
        navigate('/settings/requisite/types#newRequisiteType', {
            state: {
                initialRequisiteType: value,
            },
        })
    }, [])

    const handleSave = useCallback(async () => {
        setIsSaving(true)
        try {
            const response = await newMessageSender({
                sender,
                requisiteType: requisiteType?.code,
                channel,
            })
            if (!response?.data?.success) {
                throw new Error(
                    'errors' in response?.data ? response?.data?.errors?.general_error : 'Unknown error'
                )
            }
              
            if (response.data.data) {
                onSave?.(response.data.data)
                onClose(false)
            } else {
                throw new Error('Response data is empty')
            }
        } catch (error: any) {
            Swal.fire({ title: 'Ошибка', text: 'Произошла ошибка сохранения отправителя: ' + (getGeneralError(error) ?? 'Unknown error'), icon: 'error' })
            console.error('Ошибка сохранения отправителя:', error)
        } 
        setIsSaving(false)
    }, [channel, requisiteType, sender, onSave, onClose])

    // ===== СБРОС ПРИ ЗАКРЫТИИ =====    
    useEffect(() => {
        if (!show) {
            setRequisiteType(null)
            setChannel('SMS')
            setSender('')
        }
    }, [show])

    // ===== ПРОВЕРКА ЗАПОЛНЕННОСТИ ФОРМЫ =====
    const isFormComplete = channel && requisiteType && sender.trim().length > 2

    return (
        <Modal 
            show={show} 
            onHide={() => {!isSaving && onClose(false)}} 
            centered
            onEscapeKeyDown={(e) => {
                if (isSaving) {
                    e.preventDefault()
                    return
                }
                // if (showRequisiteTypeDropdown) {
                //     e.preventDefault()
                //     setShowRequisiteTypeDropdown(false)
                //     return
                // }
            }}
        >
            <Modal.Header closeButton>
                <Modal.Title>Новый отправитель</Modal.Title>
            </Modal.Header>
            <Modal.Body>   
                <div className="fv-row mb-8">
                    <label className="required fs-6 fw-semibold mb-2">Тип реквизита</label>
                    <Select             
                        styles={SelectStyles(theme)}         
                        onChange={handleSelectRequisiteType}
                        isClearable
                        value={requisiteType ? { value: requisiteType.code, label: requisiteType.name } : undefined}
                        options={requisiteTypes.map(v => ({value: v.code, label: v.name}))}
                        placeholder={'Начните вводить значение...'}
                        formatOptionLabel={(option, meta) => meta.context === "menu" ? (
                            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                                <span className="d-flex flex-column">
                                    <span className="fw-bold">{option.label}</span>
                                    <span className="text-muted fs-8">{option.value}</span>
                                </span>
                            </div>
                        ) : option.label}
                        noOptionsMessage={({ inputValue }) => (
                            <>
                                <div className="px-3">
                                    <div className="px-3 py-2 text-gray-500">
                                    Тип реквизита не найден
                                    </div>
                                </div>

                                <div className="separator my-2" />

                                <div className="menu-item px-3">
                                    <span
                                        className="menu-link px-3 text-primary d-flex align-items-center gap-2"
                                        onClick={() => handleAddRequisiteType(inputValue)}
                                        style={{ cursor: 'pointer' }}
                                        role="button"
                                    >
                                    <KTIcon iconName="plus" className="fs-4 text-primary" />
                                    <span>
                                        Создать тип реквизита &ldquo;{inputValue}&rdquo;
                                    </span>
                                    </span>
                                </div>
                            </>
                        )}
                    />

                    {/* Выбранный код */}
                    {requisiteType && (
                    <div className="d-flex align-items-center gap-2 mt-3">
                        <span className="text-gray-600 fs-7">Код:</span>
                        <span className="badge badge-light-success fs-7">{requisiteType.code}</span>
                    </div>
                    )}
                </div>

                {/* Канал */}
                <div className="fv-row mb-8">
                    <label className="required fs-6 fw-semibold mb-2">Канал отправки сообщений</label>
                    <Select  
                        styles={SelectStyles(theme)}                  
                        onChange={(e) => setChannel(e?.value as MessageChannel)}
                        value={{ value: channel, label: channel }}
                        options={[{value: 'SMS', label: 'SMS'},{value: 'PUSH', label: 'PUSH'}]}
                    />
                    <div className="form-text text-muted">
                        Выберите канал, в котором будет использоваться отправитель.
                    </div>
                </div>

                {/* Отправитель */}
                <div className="fv-row mb-0">
                    <label className="required fs-6 fw-semibold mb-2">Отправитель</label>

                    <input
                    className="form-control form-control-solid"
                    placeholder="Например: ACME"
                    value={sender}
                    onChange={(e) => setSender(e.target.value)}
                    />

                    <div className="form-text text-muted">
                        Имя отправителя из SMS или название пакета приложения для PUSH.
                    </div>
                </div> 
            </Modal.Body>
            <Modal.Footer>
                <Button variant='secondary' onClick={() => onClose(false)} disabled={isSaving}>Отменить</Button>
                <Button 
                    variant='primary' 
                    onClick={handleSave}
                    disabled={isSaving || !isFormComplete}
                >
                    {isSaving ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Сохранение...
                        </>
                    ) : (
                        'Сохранить'
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}